import { useMutation } from '@tanstack/react-query';
import { FieldArray, FieldArrayRenderProps, useField, useFormikContext } from 'formik';
import FileDownload from 'js-file-download';
import { useLayoutEffect, useRef, useState } from 'react';

import { ContractPriceListStatus, PriceListActivationMode } from '../../../../types/ContractMessage';
import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { validatePriceLists } from '../../../common/api/clients/contracts.api';
import { downloadGroupFile, uploadGroupFile } from '../../../common/api/clients/fileManagement.api';
import { extractFileName } from '../../../common/helpers/extractFileName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { Box } from '../../../common/ui/Box';

import { PriceListEditForm } from './PriceListEditForm';
import { PriceListEntry } from './PriceListEntry';
import { PriceListUpload } from './PriceListUpload';
import {
    applyPriceListValidationErrors,
    getNextPriceListValidFrom,
    shouldAutoApplyPriceList,
    toPriceListRequestPayload,
} from './priceListValidation.helpers';
import { PriceListFormValue } from './types';

enum EditingStateType {
    IDLE = 'IDLE',
    ADDING = 'ADDING',
    EDITING = 'EDITING',
}

type EditingState =
    | { type: EditingStateType.IDLE }
    | { type: EditingStateType.ADDING }
    | { type: EditingStateType.EDITING; index: number; isNew: boolean; original: PriceListFormValue };

interface FormikPriceListSectionProps {
    groupId: string;
    activationMode: PriceListActivationMode;
    initialValidFrom?: string;
    onBusyChange?: (isBusy: boolean) => void;
    onEditingChange?: (isEditing: boolean) => void;
    contractId?: string;
    disabled?: boolean;
}

export const FormikPriceListSection = ({
    groupId,
    activationMode,
    initialValidFrom,
    onEditingChange,
    onBusyChange,
    contractId,
    disabled,
}: FormikPriceListSectionProps) => {
    const [field] = useField<PriceListFormValue[]>('priceLists');
    const { setFieldError, setFieldTouched, setFieldValue } = useFormikContext();
    const { addErrorToast } = useToasts();

    const [editingState, setEditingState] = useState<EditingState>({ type: EditingStateType.IDLE });

    const updateEditingState = (state: EditingState) => {
        setEditingState(state);
        onEditingChange?.(state.type !== EditingStateType.IDLE);
    };

    const [isValidating, setIsValidating] = useState(false);
    const operationPending = useRef(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const arrayHelpersRef = useRef<FieldArrayRenderProps>(null);

    const fileUploadMutation = useMutation({
        mutationFn: (file: File) =>
            uploadGroupFile(file, groupId, (event) => {
                if (event.progress) {
                    setUploadProgress(event.progress * 100);
                }
            }),
        onError: () => addErrorToast(translate('global.genericError')),
        onSettled: () => {
            operationPending.current = false;
        },
        onSuccess: (response) => {
            setUploadProgress(0);
            const newEntry: PriceListFormValue = {
                fileId: response.id,
                fileName: response.fileName,
                validFrom: nextValidFrom ?? null,
                validUntil: null,
                activeOnApproval: false,
                initialStatus: ContractPriceListStatus.PROCESSING,
                status: ContractPriceListStatus.PROCESSING,
            };
            const newIndex = (field.value ?? []).length;
            arrayHelpersRef.current?.push(newEntry);
            updateEditingState({
                type: EditingStateType.EDITING,
                index: newIndex,
                isNew: true,
                original: newEntry,
            });
        },
    });

    const isBusy = fileUploadMutation.isPending || isValidating;
    useLayoutEffect(() => {
        onBusyChange?.(isBusy);
        return () => onBusyChange?.(false);
    }, [isBusy, onBusyChange]);

    const priceLists = field.value ?? [];
    const isEditing = editingState.type !== EditingStateType.IDLE;
    const lastPriceList = priceLists.length > 0 ? priceLists[priceLists.length - 1] : null;
    const lastEntryHasNoEndDate = lastPriceList !== null && lastPriceList.validUntil === null;
    const isBlockedByEndDate = activationMode === PriceListActivationMode.AUTO && lastEntryHasNoEndDate;
    const showUploadArea =
        editingState.type === EditingStateType.ADDING ||
        (editingState.type === EditingStateType.IDLE && priceLists.length === 0);
    const canAddPriceList = !isEditing && !isBlockedByEndDate && !disabled && priceLists.length > 0;
    const nextValidFrom =
        getNextPriceListValidFrom(lastPriceList?.validUntil) ??
        (priceLists.length === 0 ? initialValidFrom : undefined);

    const handleSave = async () => {
        if (editingState.type !== EditingStateType.EDITING || operationPending.current) {
            return;
        }

        operationPending.current = true;
        setIsValidating(true);
        try {
            const payload = toPriceListRequestPayload(priceLists, activationMode);
            const response = await validatePriceLists(payload, contractId);

            if (response.errors.length > 0) {
                applyPriceListValidationErrors({
                    response,
                    index: editingState.index,
                    setFieldError,
                    setFieldTouched,
                });
                return;
            }

            if (activationMode === PriceListActivationMode.AUTO) {
                setFieldValue(
                    `priceLists[${editingState.index}].activeOnApproval`,
                    shouldAutoApplyPriceList(priceLists[editingState.index]),
                    false
                );
            }

            if (activationMode === PriceListActivationMode.MANUAL && priceLists[editingState.index]?.activeOnApproval) {
                priceLists.forEach((_, index) => {
                    setFieldValue(`priceLists[${index}].activeOnApproval`, index === editingState.index, false);
                });
            }

            updateEditingState({ type: EditingStateType.IDLE });
        } catch {
            addErrorToast(translate('global.genericError'));
        } finally {
            operationPending.current = false;
            setIsValidating(false);
        }
    };

    const handleCancel = () => {
        if (editingState.type !== EditingStateType.EDITING || operationPending.current) {
            return;
        }

        if (editingState.isNew) {
            arrayHelpersRef.current?.remove(editingState.index);
        } else {
            arrayHelpersRef.current?.replace(editingState.index, editingState.original);
        }

        updateEditingState({ type: EditingStateType.IDLE });
    };

    const handleDeleteFile = () => {
        if (editingState.type !== EditingStateType.EDITING || operationPending.current) {
            return;
        }

        arrayHelpersRef.current?.remove(editingState.index);
        updateEditingState({ type: EditingStateType.IDLE });
    };

    const handleDownload = async (priceList: PriceListFormValue) => {
        try {
            const response = await downloadGroupFile(priceList.fileId);
            FileDownload(
                response.data,
                extractFileName(response.headers['content-disposition'] || '', priceList.fileName)
            );
        } catch {
            addErrorToast(translate('global.genericToastError'));
        }
    };

    return (
        <FieldArray
            name="priceLists"
            render={(arrayHelpers) => {
                arrayHelpersRef.current = arrayHelpers;

                return (
                    <Box display="flex" flexDirection="column" gap={8} inert={isBusy}>
                        {priceLists.map((priceList, index) => {
                            if (editingState.type === EditingStateType.EDITING && editingState.index === index) {
                                return (
                                    <Box
                                        key={index}
                                        padding={16}
                                        borderRadius={6}
                                        border="1px solid"
                                        borderColor="border.subtle"
                                    >
                                        <PriceListEditForm
                                            activationMode={activationMode}
                                            index={index}
                                            fileName={priceList.fileName}
                                            isSubmitting={isValidating}
                                            onSave={handleSave}
                                            onCancel={handleCancel}
                                            onDeleteFile={handleDeleteFile}
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <PriceListEntry
                                    key={index}
                                    priceList={priceList}
                                    onEdit={() =>
                                        updateEditingState({
                                            type: EditingStateType.EDITING,
                                            index,
                                            isNew: false,
                                            original: priceList,
                                        })
                                    }
                                    onDelete={() => arrayHelpersRef.current?.remove(index)}
                                    onDownload={() => handleDownload(priceList)}
                                    faded={isEditing}
                                />
                            );
                        })}

                        {showUploadArea ? (
                            <PriceListUpload
                                onFileSelected={(file) => {
                                    if (operationPending.current) {
                                        return;
                                    }
                                    operationPending.current = true;
                                    fileUploadMutation.mutate(file);
                                }}
                                uploadProgress={fileUploadMutation.isPending ? uploadProgress : undefined}
                                onCancel={
                                    editingState.type === EditingStateType.ADDING
                                        ? () => updateEditingState({ type: EditingStateType.IDLE })
                                        : undefined
                                }
                                disabled={disabled}
                            />
                        ) : null}

                        {isBlockedByEndDate && editingState.type === EditingStateType.IDLE ? (
                            <Callout description={translate('contracts.priceLists.endDateRequiredForNew')} />
                        ) : null}

                        <Box
                            marginTop={priceLists.length > 0 ? 16 : 0}
                            style={isEditing ? { opacity: 0.2, pointerEvents: 'none' } : undefined}
                        >
                            <Button
                                type="button"
                                width="100%"
                                LeadingIcon={IconPlus}
                                disabled={!canAddPriceList}
                                onClick={() => updateEditingState({ type: EditingStateType.ADDING })}
                            >
                                {translate('contracts.priceLists.addPriceList')}
                            </Button>
                        </Box>
                    </Box>
                );
            }}
        />
    );
};
